import React, { useEffect, useState } from 'react';
import { Briefcase, Users, TrendingUp, Heart, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import AnimatedSection from '../components/AnimatedSection';
import { trackButtonClick, trackPageView } from '../utils/analytics';
import { submitFormToLambda } from '../utils/api';
import { toast } from 'sonner';

const Careers = () => {
  useEffect(() => {
    trackPageView('/careers', 'Careers');
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    trackButtonClick('career_interest_submit', 'careers_form');
    
    try {
      // Submit to Lambda API
      await submitFormToLambda('careers', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        position: formData.position,
        message: formData.message
      });

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: '',
          message: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Career Growth',
      description: 'Continuous learning and advancement opportunities'
    },
    {
      icon: Heart,
      title: 'Work-Life Balance',
      description: 'Flexible hours and remote work options'
    },
    {
      icon: Users,
      title: 'Great Team',
      description: 'Collaborative and supportive work environment'
    },
    {
      icon: Briefcase,
      title: 'Competitive Pay',
      description: 'Industry-leading salaries and benefits'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A1F44] to-[#0056D2] py-20">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection animation="fadeInUp">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Join Our Team
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Help us empower businesses across India with cutting-edge cloud solutions
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                Why Work at TallyBharat?
              </h2>
              <p className="text-xl text-gray-600">
                Be part of India's leading Tally cloud hosting provider
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <AnimatedSection key={index} animation="scaleIn" delay={index * 100}>
                  <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 text-center border border-gray-100">
                    <div className="bg-gradient-to-br from-[#0056D2] to-[#0A1F44] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                      <Icon size={32} className="text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#0A1F44] mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Career Interest Form */}
      <section className="py-20 bg-gradient-to-b from-[#F5F7FA] to-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                Express Your Interest
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                We're always looking for talented individuals. Fill out the form below and we'll get in touch with you.
              </p>
            </div>
          </AnimatedSection>

          <div className="max-w-3xl mx-auto">
            <AnimatedSection animation="scaleIn">
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-[#0056D2]/20">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <h3 className="text-3xl font-bold text-green-600 mb-3">Thank You!</h3>
                    <p className="text-gray-600 text-lg">
                      We've received your interest. Our HR team will get back to you soon.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-semibold text-[#0A1F44] mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:border-[#0056D2] focus:outline-none transition-colors text-lg"
                        placeholder="Enter your full name"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-semibold text-[#0A1F44] mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:border-[#0056D2] focus:outline-none transition-colors text-lg"
                        placeholder="your.email@example.com"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-semibold text-[#0A1F44] mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        pattern="[0-9]{10}"
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:border-[#0056D2] focus:outline-none transition-colors text-lg"
                        placeholder="10-digit mobile number"
                      />
                    </div>

                    {/* Position of Interest */}
                    <div>
                      <label className="block text-sm font-semibold text-[#0A1F44] mb-2">
                        Position of Interest <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="position"
                        value={formData.position}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:border-[#0056D2] focus:outline-none transition-colors text-lg"
                        placeholder="e.g., Sales, Technical Support, Cloud Engineer"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-semibold text-[#0A1F44] mb-2">
                        Tell Us About Yourself <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="5"
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:border-[#0056D2] focus:outline-none transition-colors text-lg resize-none"
                        placeholder="Share your experience, skills, and why you'd like to join TallyBharat..."
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-[#0056D2] to-[#0A1F44] hover:from-[#0A1F44] hover:to-[#0056D2] text-white font-bold py-5 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Application'}
                      <Send className="ml-3" size={22} />
                    </Button>

                    <p className="text-center text-sm text-gray-500 mt-4">
                      By submitting this form, you agree to our privacy policy and consent to be contacted by our HR team.
                    </p>
                  </form>
                )}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
